<script setup lang="ts">
import { generateHTML } from '@tiptap/core'
import { createEmailRenderExtensions } from '../utils/tiptapExtensions'

import { fromApiResponse } from '../utils/apiTransformers'
import { headerLogoStyle } from '../utils/constants'
import type {
  EmailBlock,
  EmailHeader,
  TextBlock,
  FooterBlock,
  ButtonBlock,
  ImageBlock,
  SpacerBlock,
  SeparatorBlock
} from '../types'

const { getEmailContent } = useEmailContent()

const props = defineProps<{
  templateId: string
}>()

const loading = ref(true)
const error = ref(false)

const header = ref<EmailHeader | null>(null)
const blocks = ref<EmailBlock[]>([])
const styles = ref({
  backgroundColor: 'ffffff',
  contentBackgroundColor: 'ffffff',
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
})

const extensions = createEmailRenderExtensions()

function renderTiptapHTML(content: any): string {
  try {
    if (!content || !content.type) return ''
    return generateHTML(content, extensions)
  } catch {
    return ''
  }
}

const fetchTemplate = async () => {
  loading.value = true
  error.value = false

  try {
    const response = await getEmailContent(props.templateId)
    const parsed = fromApiResponse(response)

    header.value = parsed.header
    blocks.value = parsed.blocks
    styles.value = parsed.styles
  } catch {
    error.value = true
  } finally {
    loading.value = false
  }
}

watch(
  () => props.templateId,
  () => {
    fetchTemplate()
  },
  { immediate: true }
)

defineExpose({ refresh: fetchTemplate })
</script>

<template>
  <div class="dotted-background p-8 bg-content-bg flex items-center justify-center rounded-md">
    <div v-if="loading" class="flex h-40 items-center justify-center">
      <div class="h-5 w-5 animate-spin rounded-full border-2 border-primary/15 border-t-primary" />
    </div>

    <template v-else-if="error" />

    <div v-else class="border border-border bg-white max-h-[280px] overflow-y-auto">
      <div style="zoom: 0.45; pointer-events: none">
        <div
          :style="{
            backgroundColor: `#${styles.backgroundColor}`,
            padding: '20px',
            fontFamily: styles.fontFamily
          }"
        >
          <div
            :style="{
              backgroundColor: `#${styles.contentBackgroundColor}`,
              padding: '20px',
              maxWidth: '600px',
              margin: '0 auto',
              borderRadius: '8px'
            }"
          >
            <div v-if="header?.showLogo && header?.logo" class="mb-3 py-2" :style="{ textAlign: header.align }">
              <img :src="header.logo" alt="Logo" class="inline-block" :style="headerLogoStyle(header.logoWidth)" />
            </div>

            <div v-for="block in blocks" :key="block.id">
              <div
                v-if="block.type === 'text'"
                class="tiptap-preview"
                :style="{
                  color: `#${(block as TextBlock).color}`,
                  textAlign: (block as TextBlock).align,
                  padding: '10px 0'
                }"
                v-html="renderTiptapHTML((block as TextBlock).content)"
              />

              <div v-else-if="block.type === 'image'" class="py-2 text-center">
                <img
                  :src="(block as ImageBlock).src"
                  :alt="(block as ImageBlock).alt"
                  :style="{
                    maxWidth: (block as ImageBlock).width ? `${(block as ImageBlock).width}px` : '100%',
                    width: '100%',
                    display: 'inline-block'
                  }"
                />
              </div>

              <div
                v-else-if="block.type === 'button'"
                class="py-2"
                :style="{ textAlign: (block as ButtonBlock).align }"
              >
                <div
                  :style="{
                    display: 'inline-block',
                    backgroundColor: `#${(block as ButtonBlock).backgroundColor}`,
                    color: `#${(block as ButtonBlock).textColor}`,
                    padding: '14px 28px',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '500'
                  }"
                >
                  {{ (block as ButtonBlock).text }}
                </div>
              </div>

              <div v-else-if="block.type === 'spacer'" :style="{ height: `${(block as SpacerBlock).height}px` }" />

              <div
                v-else-if="block.type === 'separator'"
                :style="{ padding: `${(block as SeparatorBlock).height / 2}px 0` }"
              >
                <hr :style="{ borderColor: `#${(block as SeparatorBlock).color}`, borderTopWidth: '1px', margin: 0 }" />
              </div>

              <div
                v-else-if="block.type === 'footer'"
                class="py-4"
                :style="{
                  textAlign: (block as FooterBlock).align,
                  color: `#${(block as FooterBlock).color}`
                }"
              >
                <img
                  v-if="(block as FooterBlock).logoSrc"
                  :src="(block as FooterBlock).logoSrc"
                  alt="Logo"
                  style="max-height: 40px; display: inline-block; margin-bottom: 8px"
                />
                <div
                  class="tiptap-preview"
                  style="font-size: 12px"
                  v-html="renderTiptapHTML((block as FooterBlock).content)"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.tiptap-preview :deep(p) {
  margin-bottom: 0.5em;
}
.tiptap-preview :deep(h1) {
  font-size: 1.75rem;
  font-weight: 700;
  line-height: 1.2;
  margin-bottom: 0.5rem;
}
.tiptap-preview :deep(h2) {
  font-size: 1.5rem;
  font-weight: 600;
  line-height: 1.3;
  margin-bottom: 0.5rem;
}
.tiptap-preview :deep(h3) {
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}
.tiptap-preview :deep(.variable-node) {
  display: inline;
  padding: 1px 4px;
  background-color: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  font-size: 0.875em;
}
.tiptap-preview :deep(a) {
  color: #2563eb;
  text-decoration: underline;
}
</style>
